/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-report-template-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetPowerBIReportTemplatesRes, OpraReportTemplate } from '@interfaces/report-templates.interface';
import cn from 'clsx';
import { isNumber } from 'lodash';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FormFields from '@components/admin/add-custom-report-template-dialog/form-fields';
import Button from '@components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import Loading from '@components/core/loading';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import NotificationService from '@utils/notification';
import {
    useGetPowerBIModuleTypes,
    useGetPowerBIReportFormatTypes,
    useGetReportTemplateNames,
    useUpsertPowerBIReportTemplate,
} from '@apis/bff-api/report-templates';

import './styles.scss';

export type CustomReportTemplateFormInputs = {
    templateName: string;
    description: string;
    module: OpraReportTemplate['module'] | '';
    format: OpraReportTemplate['format'] | '';
    status: OpraReportTemplate['status'];
    retirementDate: string;
    pbiReportId: string;
    inUse: boolean;
    includeSignatures: boolean;
    includeNameAndVersion: boolean;
    includeReviews: boolean;
    includeNotApplicableRisks: boolean;
    includeInternalRisks: boolean;
    includeComparisonDataset: boolean;
};

type DataItem = {
    id?: string | number;
    name?: string | null;
};

const convertDataToSelectOptions = <T extends DataItem>(data?: T[]) =>
    data
        ?.filter((item) => !!item?.id && !!item?.name)
        ?.map((item) => ({
            id: item.id as string,
            text: item.name as string,
            active: true,
        }))
        ?.sort((a, b) => a.text.localeCompare(b.text)) ?? [];

const AddCustomReportTemplateDialog = ({
    orgKey,
    pbiReportTemplatesData,
    currentReportTemplate,
    onClose,
}: {
    orgKey: string;
    pbiReportTemplatesData: GetPowerBIReportTemplatesRes;
    currentReportTemplate?: OpraReportTemplate | null;
    onClose: (shouldUpdate?: boolean) => void;
}) => {
    const isEdit = !!currentReportTemplate;
    const { t } = useTranslation();
    const { trigger: upsertReportTemplate, isMutating: isMutatingReportTemplate } = useUpsertPowerBIReportTemplate();
    const { data: moduleTypesData, isLoading: isLoadingModuleTypes } = useGetPowerBIModuleTypes();
    const { data: formatTypesData, isLoading: isLoadingFormatTypes } = useGetPowerBIReportFormatTypes();
    const { data: reportTemplatesNamesData, isLoading: isLoadingReportTemplatesNames } =
        useGetReportTemplateNames(orgKey);
    const reportTemplatesNames = reportTemplatesNamesData || [];

    const isLoadingData = isLoadingModuleTypes || isLoadingFormatTypes || isLoadingReportTemplatesNames;

    const formatTypeOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...convertDataToSelectOptions(formatTypesData),
    ];
    const moduleTypeOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...convertDataToSelectOptions(moduleTypesData),
    ];
    const powerBITemplateOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...convertDataToSelectOptions(
            pbiReportTemplatesData?.filter((temp) => !temp.inUse || temp.id === currentReportTemplate?.pbiReportId),
        ),
    ];

    const formMethods = useForm<CustomReportTemplateFormInputs>({
        mode: 'all',
        defaultValues: {
            inUse: !!currentReportTemplate?.inUse,
            description: currentReportTemplate?.description ?? '',
            format: currentReportTemplate?.format ?? '',
            includeComparisonDataset: currentReportTemplate?.includeComparisonDataset ?? false,
            includeInternalRisks: currentReportTemplate?.includeInternalRisks ?? false,
            includeNameAndVersion: currentReportTemplate?.includeNameAndVersion ?? false,
            includeNotApplicableRisks: currentReportTemplate?.includeNotApplicableRisks ?? false,
            includeReviews: currentReportTemplate?.includeReviews ?? false,
            includeSignatures: currentReportTemplate?.includeSignatures ?? false,
            module: currentReportTemplate?.module ?? '',
            pbiReportId: currentReportTemplate?.pbiReportId ?? '',
            retirementDate: currentReportTemplate?.retirementDate ?? '',
            status:
                currentReportTemplate?.status ??
                (Constants.Reporting.ReportTemplateStatus.draft as OpraReportTemplate['status']),
            templateName: currentReportTemplate?.templateName ?? '',
        },
    });
    const {
        formState: { isValid, isDirty },
        handleSubmit,
    } = formMethods;

    const onSubmit = (submitData: CustomReportTemplateFormInputs) => {
        const selectedPbiTemplate = pbiReportTemplatesData?.find((pbiTemp) => pbiTemp.id === submitData.pbiReportId);
        const isModuleRAM = submitData.module === Constants.Modules.RAM;
        const reportTemplate = {
            ...submitData,
            ...(currentReportTemplate && { id: currentReportTemplate.id }),
            orgKey,
            pbiDatasetId: selectedPbiTemplate?.datasetId || undefined,
            status: isNumber(submitData.status) ? submitData.status : undefined,
            module: isNumber(submitData.module) ? submitData.module : undefined,
            format: isNumber(submitData.format) ? submitData.format : undefined,
            templateName: submitData.templateName.trim(),
            retirementDate: stringDateWithoutTimeFromLocal(submitData.retirementDate),
            // prevent module specific checks sent as checked
            includeReviews: isModuleRAM ? submitData.includeReviews : false,
            includeNotApplicableRisks: isModuleRAM ? submitData.includeNotApplicableRisks : false,
            includeInternalRisks: isModuleRAM ? submitData.includeInternalRisks : false,
            includeComparisonDataset: isModuleRAM ? false : submitData.includeComparisonDataset,
        };
        upsertReportTemplate(reportTemplate)
            .then(() => {
                if (isEdit) {
                    NotificationService.showNotification(
                        t('admin:reportTemplateSuccessfulUpdate', {
                            id: currentReportTemplate.recordId,
                        }),
                    );
                } else {
                    NotificationService.showNotification(t('admin:reportTemplateSuccessfulCreate'));
                }
                onClose(true);
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    return (
        <CustomDialog fullWidth maxWidth="md" open>
            <DialogTitle id={`${isEdit ? 'edit' : 'add'}-report-template-title`}>
                {t(isEdit ? 'admin:editCustomReportTemplate' : 'admin:addCustomReportTemplate')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <FormProvider {...formMethods}>
                <form id="report-template-form" className="report-template-form">
                    <DialogContent>
                        {isLoadingData ? (
                            <Loading />
                        ) : (
                            <FormFields
                                currentTemplate={currentReportTemplate}
                                reportTemplatesNames={reportTemplatesNames}
                                formatTypeOptions={formatTypeOptions}
                                moduleTypeOptions={moduleTypeOptions}
                                powerBITemplateOptions={powerBITemplateOptions}
                            />
                        )}
                    </DialogContent>
                    <DialogActions
                        className={cn('!k-px-5 k-mb-6', {
                            'k-flex !k-justify-content-between': isEdit,
                        })}
                    >
                        {isEdit && <ModalUpdatedInfo item={currentReportTemplate} isBFF />}
                        <Button
                            disabled={isLoadingData || isMutatingReportTemplate || !isValid || !isDirty}
                            text={t(isEdit ? 'common:update' : 'common:add')}
                            id="submit-report-template"
                            data-testid="submit-report-template"
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                        />
                    </DialogActions>
                </form>
            </FormProvider>
        </CustomDialog>
    );
};

export default AddCustomReportTemplateDialog;
